import {
  PlusOne,
  Search,
  SearchOff
} from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { getProjectActivities } from "../../api/project";
import { useTranslationHook } from "../../hooks";
import { Activity } from "../../types/model/activity";

interface ActivityLisProps {
  projectId?: number;
  onActivitySelected?: (activityId: number) => void;
  onAddActivity?: () => void;
  readOnly: boolean;

}

const ActivityList = ({
  projectId,
  onActivitySelected,
  onAddActivity,
  readOnly
}: ActivityLisProps) => {
  const { t } = useTranslationHook();

  const [activityList, setActivityList] = useState<Activity[]>([]);
  const [filteredList, setFilteredList] = useState<Activity[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [filters, setFilters] = useState({
    search: "",
  });

  const [filterAnchorEl, setFilterAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const isFilterOpen = Boolean(filterAnchorEl);

  const fetchActivities = () => {
    getProjectActivities(projectId!)
      .then((response) => {
        setActivityList(response);
        enqueueSnackbar(t("activities.activity-list-load-success"), {
          variant: "success",
        });
      })
      .catch(() => {
        enqueueSnackbar(t("activities.activity-list-load-error"), {
          variant: "error",
        });
      });
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  useEffect(() => {
    let data = [...activityList];

    if (filters.search) {
      const value = filters.search.toLowerCase();
      data = data.filter((a) =>
        a.name.toLowerCase().includes(value)
      );
    }

    setFilteredList(data);
  }, [filters, activityList]);

  const activeFilters = useMemo(() => {
    const list: { key: string; label: string }[] = [];

    if (filters.search) {
      list.push({ key: "search", label: filters.search });
    }

    return list;
  }, [filters]);

  const visibleRows = filteredList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Paper sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1 }}>
        
        <Toolbar sx={{ justifyContent: "flex-start", gap: 1 }}>
          <Typography sx={{ flex: "1 1 100%" }} variant="h5">
            {t("projects.tabs.activities")}
          </Typography>

          {/* No filters for now */}
          {/* <Tooltip title={t("generic.filter")}>
            <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
              <Badge badgeContent={activeFilters.length} color="primary">
                <FilterList color={isFilterOpen ? "primary" : undefined} />
              </Badge>
            </IconButton>
          </Tooltip> */}

          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={fetchActivities}>
              <CachedIcon />
            </IconButton>
          </Tooltip>

          {!readOnly && (
            <Tooltip title={t("generic.add")}>
              <IconButton onClick={onAddActivity}>
                <PlusOne />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>

        <TextField
          size="small"
          placeholder={t("generic.search")}
          value={filters.search}
          onChange={(e) =>
            setFilters({ ...filters, search: e.target.value })
          }
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />

        {activeFilters.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {activeFilters.map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                onDelete={() =>
                  setFilters({ ...filters, [f.key]: "" })
                }
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        )}

        <Popover
          open={isFilterOpen}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          slotProps={{ paper: { sx: { p: 3, width: 250 } } }}
        >
          <Typography>{t("generic.filter")}</Typography>
        </Popover>

        <TableContainer sx={{ flex: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("activities.name")}</TableCell>
                <TableCell>{t("admin.projects.start-date")}</TableCell>
                <TableCell>{t("admin.projects.end-date")}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 10 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: 0.6 }}>
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography variant="h6">
                        {t("activities.no-activities")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((activity) => (
                  <TableRow
                    hover
                    key={activity.id}
                    onClick={() => onActivitySelected?.(activity.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Box display="flex" gap={1.5}>
                        <ContentPasteIcon
                          sx={{
                            color: activity.closed ? "gray" : "green",
                          }}
                        />
                        {activity.name}
                      </Box>
                    </TableCell>

                    <TableCell>
                      {activity.startDate
                        ? new Intl.DateTimeFormat("pt-PT").format(
                            new Date(activity.startDate)
                          )
                        : ""}
                    </TableCell>

                    <TableCell>
                      {activity.endDate
                        ? new Intl.DateTimeFormat("pt-PT").format(
                            new Date(activity.endDate)
                          )
                        : ""}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
};

export default ActivityList;
